import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { compartilharTexto } from "@/lib/whatsapp";
import api, { brl, fmtDate, fmtTime, rotuloParcela, rotuloPagamento } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ClientForm } from "@/pages/Clients";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DOC_TYPES, DOC_MODALIDADE } from "@/lib/status";
import { ArrowLeft, Pencil, Loader2, Upload, FileText, Phone, Wallet, TrendingUp, CircleDollarSign, Trash2, Download, Handshake, FileSignature,  } from "lucide-react";
import { toast } from "sonner";
import { AcordoLinkDialog } from "@/components/AcordoLinkDialog";
import { PortalLinkDialog } from "@/components/PortalLinkDialog";
import { ComunicarClienteDialog } from "@/components/ComunicarClienteDialog";
import { ExternalLink } from "lucide-react";
import { Recolhivel } from "@/components/Recolhivel";
import { AnexoDialog } from "@/components/AnexoDialog";
import { DocCard } from "@/components/DocCard";
import { Video } from "lucide-react";
import { InstallmentsCalendar } from "@/components/InstallmentsCalendar";
import { BaixaModal } from "@/pages/Installments";
import { ClassificacaoCliente } from "@/components/ClassificacaoCliente";
import { AvaliacaoInterna } from "@/components/AvaliacaoInterna";
import { useRecurso, MSG_SEM_RECURSO } from "@/lib/recursos";
import { DeleteContractDialog } from "@/pages/ContractDetail";
import { Diff } from "@/components/AuditLog";
import { MessageCircle } from "lucide-react";

export default function ClientDetail() {
  const { id } = useParams();
  const [videoOpen, setVideoOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const temAcordos = useRecurso("acordos");
  const [data, setData] = useState(null);
  const [upTipo, setUpTipo] = useState(DOC_TYPES[0]);
  const [upFile, setUpFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [upOpen, setUpOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/clients/${id}/history`);
    setData(data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!upFile) { toast.error("Selecione um arquivo"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("tipo", upTipo);
    fd.append("modalidade", DOC_MODALIDADE[upTipo] || "");
    fd.append("file", upFile);
    try {
      await api.post(`/clients/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento enviado!");
      setUpOpen(false); setUpFile(null);
      load();
    } catch { toast.error("Erro ao enviar documento"); }
    finally { setUploading(false); }
  };

  const removeClient = async () => {
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Cliente e todos os dados excluídos (LGPD)");
      navigate("/clientes");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao excluir"); }
  };
  const exportClient = async () => {
    try {
      const resp = await api.get(`/clients/${id}/export`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(resp.data); a.download = `lgpd-${(data.cliente.nome || "cliente").split(" ")[0].toLowerCase()}-${id.slice(0, 8)}.json`; a.click();
      toast.success("Dados exportados (JSON) — LGPD");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao exportar"); }
  };
  const [termo, setTermo] = useState(null);
  const [baixa, setBaixa] = useState(null);
  const [portal, setPortal] = useState(false);
  const [comunicar, setComunicar] = useState(false);
  const [waBusy, setWaBusy] = useState(null);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin gold-text" /></div>;
  const c = data.cliente;
  const r = data.resumo;

  return (
    <div className="space-y-5 fade-up" data-testid="client-detail-page">
      <button onClick={() => navigate("/clientes")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-black">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>

      <div className="trt-card p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-14 h-14 rounded-xl bg-onyx flex items-center justify-center text-gold font-extrabold text-xl shrink-0">
              {c.nome?.[0]?.toUpperCase()}
            </div>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight">{c.nome}</h1>
              <div className="flex items-center gap-3 text-sm text-slate-500 mt-1 flex-wrap">
                <span className="mono">{c.cpf || "Sem CPF"}</span>
                <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {c.telefone || "-"}</span>
                <StatusBadge status={c.status === "ativo" ? "emprestimo_ativo" : c.status} />
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap w-full lg:w-auto" data-testid="client-actions">
            <ClientForm initial={c} onSaved={load} trigger={
              <Button variant="outline" className="gap-2 flex-1 lg:flex-none min-w-[7rem]" data-testid="edit-client-btn"><Pencil className="w-4 h-4" /> Editar</Button>
            } />
            <Button variant="outline" className="gap-2 flex-1 lg:flex-none min-w-[9rem] border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => setComunicar(true)} data-testid="client-comunicar-btn" title="Comunicar cliente (modelos de mensagem)">
              <MessageCircle className="w-4 h-4" /> Comunicar
            </Button>
            {["administrador", "financeiro"].includes(user?.role) && data?.contratos?.some((ct) => ct.status === "emprestimo_ativo") && (
              <Button variant="outline" disabled={!temAcordos} title={temAcordos ? "Fazer acordo no contrato ativo" : MSG_SEM_RECURSO} className="gap-2 flex-1 lg:flex-none min-w-[9rem] border-violet-300 text-violet-700 hover:bg-violet-50" onClick={() => navigate(`/acordos/novo?contrato_id=${data.contratos.find((ct) => ct.status === "emprestimo_ativo").id}`)} data-testid="client-fazer-acordo-btn">
                <Handshake className="w-4 h-4" /> Fazer acordo{!temAcordos && <span className="text-[10px] font-normal text-slate-500">— {MSG_SEM_RECURSO}</span>}
              </Button>
            )}
            <Button variant="outline" className="gap-2 flex-1 lg:flex-none min-w-[9rem] border-gold/60 text-onyx" onClick={() => setPortal(true)} data-testid="portal-link-btn" title="Link do Portal do Cliente">
              <ExternalLink className="w-4 h-4 gold-text" /> Link do Portal
            </Button>
            {user?.role === "administrador" && (
              <Button variant="outline" className="gap-2 flex-1 lg:flex-none min-w-[9rem]" onClick={exportClient} data-testid="export-client-btn" title="Exportar todos os dados do cliente (LGPD)">
                <Download className="w-4 h-4" /> Exportar (LGPD)
              </Button>
            )}
            {user?.role === "administrador" && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="gap-2 flex-1 lg:flex-none min-w-[9rem] text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-client-btn">
                    <Trash2 className="w-4 h-4" /> Excluir (LGPD)
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Excluir todos os dados do cliente?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Direito ao esquecimento (LGPD art. 18): remove permanentemente <b>{c.nome}</b> e todos os seus contratos, parcelas, pagamentos, acordos, solicitações e documentos. Recomendamos exportar antes. Não pode ser desfeita.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={removeClient} className="bg-red-600 hover:bg-red-700" data-testid="delete-client-confirm">Excluir definitivamente</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </div>

      {/* Summary */}
      <ClassificacaoCliente clienteId={id} refreshKey={data.pagamentos.length + data.parcelas.length} />
      <AvaliacaoInterna clienteId={id} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Sum icon={TrendingUp} label="Total Contratado" value={brl(r.total_contratado)} />
        <Sum icon={Wallet} label="Total Recebido" value={brl(r.total_recebido)} />
        <Sum icon={CircleDollarSign} label="Saldo em Aberto" value={brl(r.saldo_aberto)} />
        <Sum icon={FileText} label="Parcelas Pagas / Vencidas" value={`${r.parcelas_pagas} / ${r.parcelas_vencidas}`} />
      </div>

      <Tabs defaultValue="dados">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="dados" data-testid="ctab-dados">Dados</TabsTrigger>
          <TabsTrigger value="documentos" data-testid="ctab-documentos">Documentos ({data.documentos.length})</TabsTrigger>
          <TabsTrigger value="contratos" data-testid="ctab-contratos">Contratos ({data.contratos.length})</TabsTrigger>
          <TabsTrigger value="parcelas" data-testid="ctab-parcelas">Parcelas ({data.parcelas.length})</TabsTrigger>
          <TabsTrigger value="pagamentos" data-testid="ctab-pagamentos">Pagamentos ({data.pagamentos.length})</TabsTrigger>
          <TabsTrigger value="acordos" data-testid="ctab-acordos">Acordos ({(data.acordos || []).length})</TabsTrigger>
          <TabsTrigger value="mensagens" data-testid="ctab-mensagens">Mensagens ({(data.mensagens || []).length})</TabsTrigger>
          <TabsTrigger value="historico" data-testid="ctab-historico">Histórico ({data.historico.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="dados" className="trt-card p-6 mt-4 grid md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
          {!c.data_nascimento && <div className="md:col-span-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-sm p-3 flex flex-wrap items-center justify-between gap-2" data-testid="client-sem-nascimento-alerta">
            <span><b>Data de nascimento ausente</b> — pendência cadastral (validação de identidade). O acesso ao Portal é por CPF + senha pessoal/provisória; a data só é exigida no acesso antigo (CPF + nascimento), quando ainda habilitado.</span>
            <ClientForm initial={c} onSaved={load} trigger={<Button size="sm" variant="outline" className="border-amber-400 text-amber-900" data-testid="client-sem-nascimento-editar">Cadastrar agora</Button>} />
          </div>}
          <Row l="Data de nascimento" v={fmtDate(c.data_nascimento)} />
          <Row l="Estado civil" v={c.estado_civil} />
          <Row l="Email" v={c.email} />
          <Row l="WhatsApp" v={c.whatsapp} />
          <Row l="Endereço" v={[c.rua, c.numero, c.bairro, c.cidade, c.estado].filter(Boolean).join(", ")} />
          <Row l="CEP" v={c.cep} />
          <Row l="Possui CNPJ" v={c.possui_cnpj ? "Sim" : "Não"} />
          {c.possui_cnpj && <><Row l="CNPJ" v={c.cnpj} /><Row l="Comércio" v={c.nome_comercio} /><Row l="Tipo" v={c.tipo_comercio} /></>}
          <Row l="Observações (internas)" v={c.observacoes} />
          <Row l="Perfil" v={c.tipo_atividade === "outros" ? `Outros/CLT — ${c.subtipo_atividade === "clt" ? "CLT" : `Outro: ${c.profissao || "-"}`}` : c.tipo_atividade || "-"} />
          <Row l="Garantia informada" v={c.possui_garantia === true ? `Sim — ${c.garantia_descricao}` : c.possui_garantia === false ? "Não" : "-"} />
          {(c.contatos_familiares || []).map((ct, i) => <Row key={i} l={`Contato familiar ${i + 1}`} v={`${ct.nome} · ${ct.parentesco}${ct.vinculo ? ` (${ct.vinculo})` : ""} · ${ct.telefone}`} />)}
        </TabsContent>

        <TabsContent value="documentos" className="mt-4">
          <Dialog open={upOpen} onOpenChange={setUpOpen}>
            <DialogTrigger asChild>
              <Button className="mb-4 bg-onyx hover:bg-black text-white gap-2" data-testid="upload-doc-btn"><Upload className="w-4 h-4" /> Enviar Documento</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Enviar Documento</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Tipo de documento</Label>
                  <Select value={upTipo} onValueChange={setUpTipo}>
                    <SelectTrigger data-testid="doc-tipo-select" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Arquivo {DOC_MODALIDADE[upTipo] === "digital" ? "(somente PDF oficial)" : DOC_MODALIDADE[upTipo] === "foto" ? "(imagem JPG/PNG)" : ""}</Label>
                  <Input type="file" accept={DOC_MODALIDADE[upTipo] === "digital" ? "application/pdf,.pdf" : DOC_MODALIDADE[upTipo] === "foto" ? "image/*" : "image/*,.pdf"} onChange={(e) => setUpFile(e.target.files[0])} data-testid="doc-file-input" className="mt-1" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={upload} disabled={uploading} data-testid="doc-upload-confirm" className="bg-onyx text-white">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enviar"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <div className="flex justify-end mb-2"><Button size="sm" variant="outline" onClick={() => setVideoOpen(true)} className="gap-2" data-testid="cd-anexar-video"><Video className="w-4 h-4" /> Anexar vídeo (máx. 30s)</Button></div>
          <AnexoDialog open={videoOpen} onClose={() => setVideoOpen(false)} clienteId={id} onDone={load} />
          <Recolhivel titulo="Documentos" contagem={data.documentos.length} testid="cd-documentos"><div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.documentos.length === 0 && <p className="text-slate-400 text-sm">Nenhum documento enviado.</p>}
            {data.documentos.map((d) => <DocCard key={d.id} d={d} onChange={load} />)}
          </div></Recolhivel>
        </TabsContent>

        <TabsContent value="contratos" className="mt-4 space-y-3">
          <Recolhivel titulo="Contratos" contagem={data.contratos.length} testid="cd-contratos">
          {data.contratos.length === 0 && <p className="text-slate-400 text-sm">Nenhum contrato.</p>}
          {data.contratos.map((ct) => (
            <div key={ct.id} className="trt-card p-4 flex items-center justify-between gap-3 cursor-pointer" onClick={() => navigate(`/contratos/${ct.id}`)} data-testid={`client-contract-${ct.id}`}>
              <div><div className="font-semibold mono">{ct.numero}</div><div className="text-xs text-slate-500">{fmtDate(ct.data_contrato)} • {ct.num_parcelas}x</div></div>
              <div className="flex items-center gap-3">
                <div className="text-right"><div className="font-bold">{brl(ct.valor_total)}</div><StatusBadge status={ct.status} /></div>
                {["administrador", "financeiro"].includes(user?.role) && (
                  <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1">
                    {ct.status === "emprestimo_ativo" && <Button size="sm" variant="outline" disabled={!temAcordos} title={temAcordos ? "" : MSG_SEM_RECURSO} className="h-8 gap-1 border-violet-300 text-violet-700 hover:bg-violet-50" onClick={() => navigate(`/acordos/novo?contrato_id=${ct.id}`)} data-testid={`client-fazer-acordo-${ct.id}`}><Handshake className="w-4 h-4" /> <span className="hidden sm:inline">Fazer acordo</span></Button>}
                    <DeleteContractDialog contract={ct} onDeleted={load} trigger={
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:bg-red-50" title="Excluir contrato (vai para a Lixeira)" data-testid={`client-delete-contract-${ct.id}`}><Trash2 className="w-4 h-4" /></Button>
                    } />
                  </div>
                )}
              </div>
            </div>
          ))}
          </Recolhivel>
        </TabsContent>

        <TabsContent value="parcelas" className="mt-4 space-y-4">
          <InstallmentsCalendar clienteId={id} refreshKey={data.parcelas.length + data.pagamentos.length} onBaixa={(p) => setBaixa(p)} />
          <MiniTable rows={data.parcelas} cols={["numero", "vencimento", "valor", "status"]}
            render={(p) => [rotuloParcela(p).replace("Parcela #", "#"), fmtDate(p.vencimento), brl(p.valor), <StatusBadge key="status" type="inst" status={p.status} vencimento={p.vencimento} />]} />
          <BaixaModal inst={baixa} onClose={() => setBaixa(null)} onDone={load} />
        </TabsContent>

        <TabsContent value="pagamentos" className="mt-4">
          <Recolhivel titulo="Recibos / pagamentos" contagem={data.pagamentos.length} testid="cd-pagamentos"><MiniTable rows={data.pagamentos} cols={["data", "parcela", "forma", "valor"]}
            render={(p) => [fmtDate(p.data), rotuloPagamento(p).replace("Parcela #", "#"), p.forma_pagamento, brl(p.valor_pago)]} /></Recolhivel>
        </TabsContent>

        <TabsContent value="acordos" className="mt-4 space-y-3">
          {(data.acordos || []).length === 0 && <p className="text-slate-400 text-sm">Nenhum acordo de renegociação.</p>}
          {(data.acordos || []).map((a) => (
            <div key={a.id} className="trt-card p-4 space-y-2" data-testid={`acordo-card-${a.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-semibold flex items-center gap-2"><Handshake className="w-4 h-4 gold-text" /> Acordo de {fmtDate(a.created_at)}
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${a.assinatura ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`} data-testid={`acordo-sig-${a.id}`}>
                      {a.assinatura ? `Assinado ${fmtDate(a.assinatura.data)}` : "Aguardando assinatura"}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Parcelas antigas: nº {(a.parcelas_antigas || []).join(", ")} • saldo {brl(a.saldo_renegociado)} + encargos {brl(a.acrescimo)} = <b>{brl(a.total)}</b> em {a.num_parcelas}x • por {a.criado_por}</div>
                  {a.observacao && <div className="text-xs text-slate-500 italic">&ldquo;{a.observacao}&rdquo;</div>}
                </div>
                <Button size="sm" variant="outline" className="gap-1 border-violet-200 text-violet-700 hover:bg-violet-50" onClick={() => setTermo(a)} data-testid={`acordo-termo-${a.id}`}><FileSignature className="w-4 h-4" /> Termo / Link</Button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {(a.novas_parcelas || []).map((p) => (
                  <div key={p.numero} className="rounded-lg bg-slate-50 px-3 py-2 text-xs flex items-center justify-between">
                    <span>{rotuloParcela(p).replace("Parcela #", "#")} • {fmtDate(p.vencimento)}</span><span className="flex items-center gap-1"><b className="mono">{brl(p.valor)}</b><StatusBadge type="inst" status={p.status} vencimento={p.vencimento} /></span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="mensagens" className="mt-4 space-y-2">
          {(data.mensagens || []).length === 0 && <p className="text-slate-400 text-sm">Nenhuma mensagem gerada para este cliente.</p>}
          {(data.mensagens || []).map((m) => (
            <div key={m.id} className="trt-card p-3 text-sm" data-testid={`client-msg-${m.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-medium flex items-center gap-2"><MessageCircle className="w-4 h-4 gold-text" /> {m.titulo || m.evento}
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${m.status === "enviada" ? "bg-emerald-100 text-emerald-800" : m.status === "falhou" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{m.status}</span>
                  {m.canal && <span className="text-[11px] text-slate-400">{m.canal}</span>}
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">{fmtDate(m.created_at)}
                  {m.status !== "enviada" && <button disabled={waBusy === m.id} onClick={async () => { if (waBusy) return; setWaBusy(m.id); await compartilharTexto({ texto: m.texto, titulo: m.titulo, mensagem_id: m.id }); await load(); setWaBusy(null); }} className="text-emerald-700 font-semibold hover:underline disabled:opacity-50" data-testid={`client-msg-share-${m.id}`}>{waBusy === m.id ? "Registrando..." : "Copiar / Compartilhar"}</button>}
                </div>
              </div>
              <p className="text-slate-600 whitespace-pre-wrap mt-1 text-xs line-clamp-3">{m.texto}</p>
              {m.erro && <p className="text-red-600 text-xs mt-1">{m.erro}</p>}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="historico" className="mt-4 trt-card p-4 space-y-2">
          {data.historico.length === 0 && <p className="text-slate-400 text-sm">Sem alterações registradas.</p>}
          {data.historico.map((h) => (
            <details key={h.id} className="text-sm border-b pb-2" data-testid={`client-hist-${h.id}`}>
              <summary className="cursor-pointer list-none flex items-center gap-2 flex-wrap">
                <span className="font-medium">{h.usuario}</span> <span className="text-slate-500">{h.acao}</span>
                <span className="text-[11px] px-1.5 rounded bg-slate-100 text-slate-500">{h.entidade}</span>
                <span className="text-slate-400 text-xs ml-auto">{fmtDate(h.data)} {fmtTime(h.data)}</span>
              </summary>
              {(h.antes || h.depois) && <div className="mt-2 pl-2 border-l-2 border-gold/40"><Diff antes={h.antes} depois={h.depois} /></div>}
            </details>
          ))}
        </TabsContent>
      </Tabs>
      {termo && <AcordoLinkDialog acordo={termo} telefone={c.telefone} onClose={() => setTermo(null)} />}
      {portal && <PortalLinkDialog cliente={c} onClose={() => setPortal(false)} />}
      {comunicar && <ComunicarClienteDialog contexto="cliente" clienteId={c.id} onClose={() => setComunicar(false)} />}
    </div>
  );
}

const Sum = ({ icon: Icon, label, value }) => (
  <div className="trt-card p-4"><Icon className="w-5 h-5 gold-text mb-2" /><div className="text-lg font-extrabold mono">{value}</div><div className="text-xs text-slate-500">{label}</div></div>
);
const Row = ({ l, v }) => (<><div className="text-slate-500 py-1">{l}</div><div className="py-1 font-medium">{v || "-"}</div></>);

const MiniTable = ({ rows, cols, render }) => (
  <div className="trt-card overflow-hidden">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>{cols.map((c) => <th key={c} className="text-left px-4 py-2">{c}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 ? <tr><td colSpan={cols.length} className="py-6 text-center text-slate-400">Nada aqui.</td></tr> :
          rows.map((r, i) => <tr key={r.id || i} className="border-t">{render(r).map((cell, j) => <td key={cols[j] || j} className="px-4 py-2">{cell}</td>)}</tr>)}
      </tbody>
    </table>
  </div>
);
